import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react'
import { useHistory } from 'react-router-dom'
import { toast, ToastOptions } from 'react-toastify'

import {
  RelacaoDocumentosLicencaAluno,
  UploadDocumentos,
  UploadDocumentosCertificacao
} from 'src/compartilhados'
import { ResumoDeDocumentosMultiplosDeEntradaTurmaCertificacaoMapper } from 'src/compartilhados/modulos/gestao-de-documentos/mappers/resumo-de-documentos.mapper'
import {
  Carregando,
  ErroContainerLayout,
  ErroLayoutContainer,
  Modal,
  ModalRef
} from 'src/componentes'
import { RotasAluno } from 'src/rotas/aluno'
import { Api } from 'src/servicos'
import { Documento } from 'src/tipos'

import { ContainerDaPagina, Dicas, TituloComplementar } from './style'

const erroAoBaixarDocumentos = 'Erro ao baixar documentos.'
const ErroAoObterDocumentos = 'Não foi possível obter os documentos do aluno.'

const toastOptions = { type: 'error' } as ToastOptions

export type DocumentosTurmaCertificacaoHandles = {
  enviarDocumentos: () => void
}

interface DocumentosTurmaCertificacaoProps {
  turmaCertificacaoId: string | undefined // ou substitua `any` pelo tipo apropriado
  documentos: UploadDocumentos[]
  atualizar: (novosDocumentos: UploadDocumentos) => void
  reiniciar: (novosDocumentos: UploadDocumentos[]) => void
}

const DocumentosTurmaCertificacao = forwardRef<
  DocumentosTurmaCertificacaoHandles,
  DocumentosTurmaCertificacaoProps
>(({ turmaCertificacaoId, documentos, atualizar, reiniciar }) => {
  const history = useHistory()
  const [pronto, definirPronto] = useState<boolean>()
  const modalDeCancelamento = useRef<ModalRef>(null)

  const { limparErro, definirErro, erro } = ErroLayoutContainer.useContainer()

  const tiposLicenca = useMemo(
    () => RelacaoDocumentosLicencaAluno.map(x => x.tipo),
    [RelacaoDocumentosLicencaAluno]
  )

  const obterResumoDeDocumentos = async (idDoUsuario: string) => {
    try {
      definirPronto(false)
      limparErro()
      const resposta = await Api.ObterResumoDeDocumentosTurmaCertificacao(
        idDoUsuario
      )

      const resultado = ResumoDeDocumentosMultiplosDeEntradaTurmaCertificacaoMapper(
        documentos,
        resposta
      )

      reiniciar(resultado)
    } catch (erro) {
      const mensagens = ErroAoObterDocumentos
      const acaoVoltar = () => history.goBack()
      definirErro({ mensagens, acaoVoltar })
    } finally {
      definirPronto(true)
    }
  }

  const abrirImagemNovaAba = (documentoAluno: Documento) => {
    const novaAba = window.open('')
    if (documentoAluno.contentType === 'application/pdf') {
      novaAba.document.write(
        `<iframe width='100%' height='100%' src='data:${documentoAluno?.contentType};base64, ` +
          encodeURI(documentoAluno?.base64) +
          "' />"
      )
    } else {
      const img = `<img src='${`data:${documentoAluno?.contentType};base64, ${documentoAluno?.base64}`}' />`
      novaAba.document.write(img)
    }
    novaAba.document.title = 'Documento da turma'
  }

  const visualizarDocumento = async (documentoId: string) => {
    try {
      const novoDocumento = await Api.RequisitarDocumentoTurmaCertificacao(
        turmaCertificacaoId,
        documentoId
      )
      if (novoDocumento) {
        abrirImagemNovaAba(novoDocumento)
      }
    } catch (error) {
      toast(ErroAoObterDocumentos, toastOptions)
    }
  }

  const excluirDocumento = async (documentoId: string) => {
    try {
      await Api.RemoverDocumentoTurmaCertificacao(
        turmaCertificacaoId,
        documentoId
      )
      reiniciar(documentos.filter(doc => doc.id !== documentoId))
    } catch (error) {
      toast(ErroAoObterDocumentos, toastOptions)
    }
  }

  const obterDownloadDeDocumentos = async (
    documentoId: string
  ): Promise<void> => {
    try {
      await Api.DownloadDocumentosTurmaCertificacao(
        turmaCertificacaoId,
        documentoId
      )
    } catch (erro) {
      toast(erroAoBaixarDocumentos, toastOptions)
    }
  }

  const documentosTurma = useMemo(() => {
    return documentos.filter(x => !tiposLicenca.some(y => y === x.tipo))
  }, [documentos])

  useEffect(() => {
    if (turmaCertificacaoId) {
      obterResumoDeDocumentos(turmaCertificacaoId)
    }
    definirPronto(true)
  }, [])

  return pronto && !erro ? (
    <ContainerDaPagina>
      <TituloComplementar>Envio de PPC</TituloComplementar>
      <Dicas>O envio de PPC é obrigatório para abertura de turma</Dicas>
      {documentosTurma.map(documento => (
        <UploadDocumentosCertificacao
          key={documento.id}
          documento={documento}
          eventoDeMudanca={atualizar}
          eventoDeDownload={obterDownloadDeDocumentos}
          eventoDeVisualizacao={visualizarDocumento}
          eventoDeExclusao={excluirDocumento}
        />
      ))}
      <Modal
        ref={modalDeCancelamento}
        backdrop
        id="modal-de-cancelamento"
        titulo={'Deseja cancelar?'}
        acaoPrimaria={{
          titulo: 'Sim',
          tipo: 'button',
          acao: () => history.push(RotasAluno.Dashboard)
        }}
        acaoSecundario={{
          titulo: 'Não',
          tipo: 'button',
          acao: () => modalDeCancelamento?.current?.fechar()
        }}
      >
        <p>Selecione uma opção</p>
      </Modal>
    </ContainerDaPagina>
  ) : !pronto && !erro ? (
    <Carregando texto="Carregando documentos da turma..." />
  ) : (
    <ErroContainerLayout />
  )
})

export default DocumentosTurmaCertificacao
